import {
  AMENITY_CATALOG,
  AMENITY_GROUPS,
  ENERGY_CARRIER_LABELS,
  HEATING_SYSTEM_LABELS,
  KFW_STANDARD_LABELS,
  amenityGroupLabels,
  derivePriceShares,
  energyCertificateTypeLabels,
  formatEuro,
  formatPercent,
  unitSubtypeLabels,
  unitUsageLabels,
  type AmenityGroup,
  type CalculationAssumptions,
  type PropertyCalcDefaults,
  type TenancyRecord,
  type UnitCostItemRecord,
} from '@brimo/shared';
import type { DocumentRowItem } from '../bits';
import { fileMeta, formatNumber } from '../format';
import type { DocumentRow } from '../../contacts/types';
import { calcValueLabel } from '../record/calcDefaultsDraft';
import type { UnitFields } from '../types';

/**
 * Display values of the unit's extended data (data model extension 0029): the extra rows of
 * „Einheitendaten", the groups of „Ausstattung & Energie", the price composition and the cost items.
 */

const DASH = '—';

export function textOrDash(value: string | null | undefined): string {
  return value === null || value === undefined || value.trim() === '' ? DASH : value;
}

/** The unit's own address („Lindenstraße 4a"), null when the unit uses the property address. */
export function ownAddress(fields: Pick<UnitFields, 'street' | 'houseNumber'>): string | null {
  const parts = [fields.street, fields.houseNumber].filter((part): part is string => Boolean(part?.trim()));
  return parts.length > 0 ? parts.join(' ') : null;
}

export interface FactRow {
  label: string;
  value: string;
}

/**
 * The new rows of „Einheitendaten", only those with a value (a unit without the extended data keeps the
 * record of the screens). „Vertriebsfreigabe" shows when the unit is not released or comes from TeamProQ.
 */
export function extendedFactRows(fields: UnitFields, imported = false): FactRow[] {
  const rows: FactRow[] = [];
  if (fields.subtype) rows.push({ label: 'Untertyp', value: unitSubtypeLabels[fields.subtype] });
  if (fields.usage) rows.push({ label: 'Nutzung', value: unitUsageLabels[fields.usage] });
  const address = ownAddress(fields);
  if (address) rows.push({ label: 'Adresse', value: address });
  if (fields.floorPosition?.trim()) rows.push({ label: 'Lage im Geschoss', value: fields.floorPosition });
  if (fields.bathrooms !== null) rows.push({ label: 'Bäder', value: formatNumber(fields.bathrooms, 0) });
  if (fields.landAreaSqm !== null)
    rows.push({ label: 'Grundstücksfläche', value: `${formatNumber(fields.landAreaSqm, 1)} m²` });
  if (!fields.salesReleased || imported)
    rows.push({ label: 'Vertriebsfreigabe', value: fields.salesReleased ? 'Ja' : 'Nein' });
  return rows;
}

/**
 * The calculator's cost and subsidy facts of the unit (owner decision 19.10.2026, D-273): only the rows with a
 * value, the flag „Verkäufer zahlt Kaufnebenkosten" only when set. „Hausgeld" stays a separate fact.
 */
export function calculatorFactRows(fields: UnitFields): FactRow[] {
  const rows: FactRow[] = [];
  const monthly = (label: string, cents: number | null) => {
    if (cents !== null) rows.push({ label, value: `${formatEuro(cents)} mtl.` });
  };
  monthly('SE-Verwaltung', fields.sevCents);
  monthly('WEG-Verwaltung', fields.wegCents);
  monthly('Sonstige nicht umlagefähige Kosten', fields.otherNonRecoverableCents);
  monthly('Stellplatzmiete', fields.parkingSpaceRentCents);
  if (fields.renovationSubsidyCents !== null)
    rows.push({ label: 'Renovierungszuschuss', value: formatEuro(fields.renovationSubsidyCents) });
  if (fields.maintenanceExpenseCents !== null)
    rows.push({ label: 'Erhaltungsaufwand einmalig', value: formatEuro(fields.maintenanceExpenseCents) });
  if (fields.sellerPaysAcquisitionCosts) rows.push({ label: 'Verkäufer zahlt Kaufnebenkosten', value: 'Ja' });
  return rows;
}

export interface AmenityGroupView {
  group: AmenityGroup;
  label: string;
  items: string[];
}

/** Selected amenities per group in catalog order; groups without a selection are left out. */
export function amenityGroups(amenities: UnitFields['amenities']): AmenityGroupView[] {
  return AMENITY_GROUPS.flatMap((group) => {
    const selected = new Set<string>(amenities[group] ?? []);
    const items = Object.entries(AMENITY_CATALOG[group])
      .filter(([key]) => selected.has(key))
      .map(([, label]) => label);
    return items.length > 0 ? [{ group, label: amenityGroupLabels[group], items }] : [];
  });
}

function labelOf(map: Readonly<Record<string, string>>, key: string): string {
  return map[key] ?? key;
}

/** Rows of the energy block of „Ausstattung & Energie". */
export function energyRows(fields: UnitFields): FactRow[] {
  return [
    {
      label: 'Energieausweis',
      value: fields.energyCertificateType ? energyCertificateTypeLabels[fields.energyCertificateType] : DASH,
    },
    {
      label: 'Kennwert',
      value:
        fields.energyValueKwhSqm === null ? DASH : `${formatNumber(fields.energyValueKwhSqm, 2)} kWh/(m²·a)`,
    },
    { label: 'Energieklasse', value: textOrDash(fields.energyClass) },
    {
      label: 'Energieträger',
      value:
        fields.energyCarriers.length === 0
          ? DASH
          : fields.energyCarriers.map((key) => labelOf(ENERGY_CARRIER_LABELS, key)).join(', '),
    },
    {
      label: 'Heizung',
      value:
        fields.heatingSystems.length === 0
          ? DASH
          : fields.heatingSystems.map((key) => labelOf(HEATING_SYSTEM_LABELS, key)).join(', '),
    },
    {
      label: 'KfW-Standard',
      value: fields.kfwStandard ? labelOf(KFW_STANDARD_LABELS, fields.kfwStandard) : DASH,
    },
  ];
}

/** True when the card „Ausstattung & Energie" has anything to show. */
export function hasAmenitiesOrEnergy(fields: UnitFields): boolean {
  return (
    amenityGroups(fields.amenities).length > 0 ||
    fields.energyCertificateType !== null ||
    fields.energyValueKwhSqm !== null ||
    fields.energyClass !== null ||
    fields.energyCarriers.length > 0 ||
    fields.heatingSystems.length > 0 ||
    fields.kfwStandard !== null
  );
}

export interface CompositionRow {
  label: string;
  cents: number | null;
}

/** „Kaufpreisaufteilung" in the order of the purchase contract. */
export function compositionRows(fields: UnitFields): CompositionRow[] {
  return [
    { label: 'Grund und Boden', cents: fields.priceLandCents },
    { label: 'Altbausubstanz', cents: fields.priceExistingBuildingCents },
    { label: 'Sanierung', cents: fields.renovationCents },
    { label: 'Neubau', cents: fields.priceNewConstructionCents },
    { label: 'Möblierung', cents: fields.priceFurnitureCents },
    { label: 'Außenanlagen', cents: fields.priceOutdoorFacilitiesCents },
  ];
}

export function hasComposition(fields: UnitFields): boolean {
  return compositionRows(fields).some((row) => row.cents !== null);
}

/** The derived shares („Anteil Grund und Boden 18,4 %"), from the stored values or derived from the parts. */
export function compositionShares(fields: UnitFields): FactRow[] {
  const derived = derivePriceShares(fields, fields.priceCents);
  const land = fields.landSharePct ?? derived.landSharePct;
  const oldBuilding = fields.oldBuildingSharePct ?? derived.oldBuildingSharePct;
  const rows: FactRow[] = [];
  if (land !== null) rows.push({ label: 'Anteil Grund und Boden', value: formatPercent(land, 1) });
  if (oldBuilding !== null)
    rows.push({ label: 'Anteil Altbausubstanz', value: formatPercent(oldBuilding, 1) });
  if (fields.depreciationPct !== null)
    rows.push({ label: 'AfA-Satz', value: formatPercent(fields.depreciationPct, 2) });
  return rows;
}

/** „1.250 €", „3,5 %" or „—"; monthly items get „mtl.". */
export function costAmountLabel(item: Pick<UnitCostItemRecord, 'kind' | 'amountCents' | 'percent'>): string {
  if (item.amountCents !== null) {
    return item.kind === 'monthly' ? `${formatEuro(item.amountCents)} mtl.` : formatEuro(item.amountCents);
  }
  if (item.percent !== null) return formatPercent(item.percent, 2);
  return DASH;
}

/** Sum of the amounts of one list (percent items are not added up); null when no item has an amount. */
export function costSum(items: readonly Pick<UnitCostItemRecord, 'amountCents'>[]): number | null {
  const amounts = items.map((item) => item.amountCents).filter((cents): cents is number => cents !== null);
  return amounts.length === 0 ? null : amounts.reduce((a, b) => a + b, 0);
}

/** The ids of all items after moving one inside its list (the order route takes the unit's whole order). */
export function reorderCostItems(items: readonly UnitCostItemRecord[], id: string, step: -1 | 1): string[] {
  const ordered = [...items].sort((a, b) => a.position - b.position);
  const moving = ordered.find((item) => item.id === id);
  if (!moving) return ordered.map((item) => item.id);
  const sameKind = ordered.filter((item) => item.kind === moving.kind);
  const from = sameKind.indexOf(moving);
  const to = from + step;
  if (to < 0 || to >= sameKind.length) return ordered.map((item) => item.id);
  const swapped = [...sameKind];
  swapped.splice(from, 1);
  swapped.splice(to, 0, moving);
  const queue = [...swapped];
  return ordered.map((item) => (item.kind === moving.kind ? (queue.shift() ?? item) : item).id);
}

/** Active tenancy first, then the history by begin (newest first). */
export function splitTenancies(items: readonly TenancyRecord[]): {
  active: TenancyRecord | null;
  history: TenancyRecord[];
} {
  const sorted = [...items].sort((a, b) => (b.beginOn ?? '').localeCompare(a.beginOn ?? ''));
  const active = sorted.find((item) => item.status === 'active') ?? null;
  return { active, history: sorted.filter((item) => item !== active) };
}

/** The rental agreement slot first, then the other documents of the tenancy newest first; archived versions hidden. */
export function tenancyDocuments(rows: readonly DocumentRow[], tenancyId: string): DocumentRow[] {
  const rank = (row: DocumentRow) => (row.documentType?.key === 'rental_agreement' ? 0 : 1);
  return rows
    .filter((row) => row.owner.type === 'tenancy' && row.owner.id === tenancyId && row.status !== 'archived')
    .sort((a, b) => rank(a) - rank(b) || b.uploadedAt.localeCompare(a.uploadedAt));
}

/**
 * Row of the tenancy card: „Mietvertrag · fehlt" for the empty slot, else the file with its type
 * („Mieterhöhungsverlangen · PDF · 1,2 MB"); clean files open in a new tab.
 */
export function tenancyDocumentItem(row: DocumentRow): DocumentRowItem {
  if (row.fileId === null) {
    return { key: row.id, name: row.documentType?.name ?? row.name, meta: row.statusLabel, tone: 'missing' };
  }
  const typeName = row.documentType?.name;
  const meta = fileMeta(row);
  const item: DocumentRowItem = {
    key: row.id,
    name: row.name,
    meta: typeName && typeName !== row.name ? `${typeName} · ${meta}` : meta,
  };
  if (row.canOpen) item.href = `/api/files/${row.fileId}`;
  return item;
}

// --- inheritance hints (D-363) ------------------------------------------------------------------------

/** Unit fields that inherit from the property's „Objekt-Vorgaben" and the organization when left empty. */
export type UnitInheritedField =
  'depreciationPct' | 'sevCents' | 'wegCents' | 'reserveShareCents' | 'otherNonRecoverableCents';

export type UnitInheritanceHints = Partial<Record<UnitInheritedField, string>>;

/**
 * The muted hint under an inherited unit field while it is empty (Nico Kremer's feedback 01.11.2026, D-363):
 * „Leer = Objekt-Vorgabe (4,5 %)" when the property has a value, else „Leer = Vorgabe der Organisation (2 %)";
 * SE-/WEG-Verwaltung have no organization setting: „Leer = Standardwert (0,00 €/Mon.)". The same order as the
 * calculator (`calculatorDefaults`, D-359; TeamProQ cost items of the unit still win over both). `org` null (no read
 * access to the assumptions) leaves the organization-level hints out. Rendered by the unit dialog (UNITUX).
 */
export function unitInheritanceHints(
  property: Pick<
    PropertyCalcDefaults,
    'depreciationPct' | 'sevCents' | 'wegCents' | 'reservePerSqmCents' | 'otherCostsPerSqmCents'
  >,
  org: Pick<
    CalculationAssumptions,
    'calcDepreciationPct' | 'calcReservePerSqmCents' | 'calcOtherCostsPerSqmCents'
  > | null,
): UnitInheritanceHints {
  const hints: UnitInheritanceHints = {};
  const fromProperty = (value: string) => `Leer = Objekt-Vorgabe (${value})`;
  const fromOrganization = (value: string) => `Leer = Vorgabe der Organisation (${value})`;
  const chain = (
    field: UnitInheritedField,
    propertyValue: number | null,
    key: Parameters<typeof calcValueLabel>[0],
    orgValue: number | null | undefined,
  ) => {
    if (propertyValue !== null) hints[field] = fromProperty(calcValueLabel(key, propertyValue));
    else if (orgValue !== null && orgValue !== undefined)
      hints[field] = fromOrganization(calcValueLabel(key, orgValue));
  };
  chain('depreciationPct', property.depreciationPct, 'depreciationPct', org?.calcDepreciationPct);
  chain('reserveShareCents', property.reservePerSqmCents, 'reservePerSqmCents', org?.calcReservePerSqmCents);
  chain(
    'otherNonRecoverableCents',
    property.otherCostsPerSqmCents,
    'otherCostsPerSqmCents',
    org?.calcOtherCostsPerSqmCents,
  );
  for (const field of ['sevCents', 'wegCents'] as const) {
    const value = property[field];
    hints[field] =
      value !== null
        ? fromProperty(calcValueLabel(field, value))
        : `Leer = Standardwert (${calcValueLabel(field, 0)})`;
  }
  return hints;
}
